import { MailIcon, MonitorSmartphoneIcon, VolumeXIcon } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import type { ReportRecipientPreview } from '@/lib/reports';
import { cn } from '@/lib/utils';

/** Resolved audience of a draft: who gets what, and who has switched it off. */
export function RecipientList({ recipients, max = 12 }: { recipients: ReportRecipientPreview[]; max?: number }) {
  if (recipients.length === 0) return <p className="text-destructive text-sm">Nobody matches this audience right now. Check the roles or add people.</p>;
  const shown = recipients.slice(0, max);
  const silent = recipients.filter((r) => r.channels.length === 0).length;
  return (
    <div className="space-y-2">
      <ul className="divide-y border text-sm">
        {shown.map((r) => (
          <li key={`${r.kind}-${r.id ?? r.email}`} className={cn('flex items-center gap-3 px-3 py-1.5', r.channels.length === 0 && 'opacity-60')}>
            <span className="min-w-0 flex-1 truncate">
              {r.name}
              {r.kind !== 'external' && r.email !== r.name && <span className="text-muted-foreground text-xs"> · {r.email}</span>}
              {r.kind === 'external' && <span className="text-muted-foreground text-xs"> · outside address</span>}
            </span>
            <span className="flex shrink-0 items-center gap-1 text-xs">
              {r.channels.includes('email') && <MailIcon className="text-muted-foreground size-3.5" aria-label="Email" />}
              {r.channels.includes('in_app') && <MonitorSmartphoneIcon className="text-muted-foreground size-3.5" aria-label="In-app" />}
              {r.muted ? (
                <Badge variant="muted" className="gap-1">
                  <VolumeXIcon className="size-3" aria-hidden /> muted this report
                </Badge>
              ) : r.channels.length === 0 ? (
                <Badge variant="muted">notifications off</Badge>
              ) : r.emailOff && r.kind !== 'external' ? (
                <Badge variant="outline">email off</Badge>
              ) : r.inAppOff && r.kind !== 'external' ? (
                <Badge variant="outline">in-app off</Badge>
              ) : null}
            </span>
          </li>
        ))}
      </ul>
      {(recipients.length > shown.length || silent > 0) && (
        <p className="text-muted-foreground text-xs">
          {recipients.length > shown.length ? `${recipients.length - shown.length} more. ` : ''}
          {silent > 0 ? `${silent} ${silent === 1 ? 'person has' : 'people have'} this switched off and will not receive it.` : ''}
        </p>
      )}
    </div>
  );
}
